import React, { useEffect, useRef, useState } from 'react';
import { googleReviews, translations, restaurantInfo } from '../data/mock';
import { Star, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const ReviewsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const { language } = useLanguage();
  const t = translations[language];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const getLangLabel = (lang) => {
    switch(lang) {
      case 'DE': return 'Deutsch';
      case 'FR': return 'Français';
      case 'IT': return 'Italiano';
      case 'EN': return 'English';
      default: return lang;
    }
  };

  return (
    <section
      ref={sectionRef}
      className="py-28 bg-gradient-to-b from-[#e8dcc8] via-[#f0e6d3] to-[#e8dcc8] relative overflow-hidden"
    >
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#c9a227]/8 rounded-full blur-[100px]"></div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="inline-block text-[#8b7119] font-medium tracking-[0.2em] uppercase text-sm mb-4">{t.reviews.subtitle}</span>
          <h2 className={`font-serif text-4xl md:text-5xl font-bold text-[#3d2617] mb-6 transition-all duration-700 ${isVisible ? 'section-title-glow visible' : ''}`}>
            {t.reviews.title}
          </h2>
          <div className={`w-24 h-0.5 bg-[#c9a227] mx-auto mb-6 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-0'}`}></div>
          
          {/* Google Reviews Link */}
          <a 
            href={restaurantInfo.googleReviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 text-[#5c3d2e] hover:text-[#c9a227] transition-colors"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span className="font-medium">{t.reviews.source}</span>
          </a>
        </div>

        {/* Reviews grid with illuminate effect */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {googleReviews.map((review, index) => (
            <div
              key={review.name}
              className={`bg-white p-8 shadow-lg transition-all duration-700 hover:shadow-xl hover:shadow-[#c9a227]/15 hover:-translate-y-2 ${isVisible ? 'card-illuminate visible' : 'card-illuminate'}`}
              style={{ transitionDelay: `${300 + index * 100}ms` }}
            >
              {/* Stars */}
              <div className="flex gap-1 mb-4">
                {[...Array(review.rating)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-[#FBBC05] text-[#FBBC05]" />
                ))}
              </div>

              {/* Text */}
              <p className="text-[#3d2617] leading-relaxed mb-6 font-elegant text-lg">
                "{review.text[language]}"
              </p>

              {/* Author */}
              <div className="flex items-center justify-between pt-4 border-t border-[#e8dcc8]">
                <div>
                  <p className="font-semibold text-[#3d2617]">{review.name}</p>
                  <p className="text-sm text-[#8b6b5a]">{review.date}</p>
                </div>
                <span className="text-xs border border-[#c9a227]/30 text-[#8b7119] px-3 py-1 font-medium">
                  {getLangLabel(review.lang)}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* CTA to Google Reviews */}
        <div className={`text-center transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <a
            href={restaurantInfo.googleReviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-white text-[#3d2617] px-8 py-4 font-semibold border-2 border-[#3d2617] hover:bg-[#3d2617] hover:text-white transition-all duration-300 hover:shadow-[0_0_20px_rgba(61,38,23,0.2)]"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t.reviews.seeAll}
            <ExternalLink size={16} />
          </a>
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
